import { useTranslation } from 'react-i18next';
import { useState, useEffect } from 'react';
import styles from './EditJob.module.css';
import FormField from '../../../components/UI/UpdatedFormUI/FormField/FormField';
import BubbleButton from '../../../components/UI/Buttons/BubbleButton/BubbleButton';
import FormSelectField from '../../../components/UI/UpdatedFormUI/FormSelectField/FormSelectField';
import FormOptionField from '../../../components/UI/UpdatedFormUI/FormOptionField/FormOptionField';
import { useGetSkillsHook, useGetEducationLevelsHook, useGetRoleBasedSkillsHook, useGetJobCategoriesHook } from '../../../utils/utilityHooks';

function QualificationAndSkills({ formik, activeSteps, setActiveSteps, saveAsDraftOnly, updateJobOnly, id, draftLoading, submitLoading, formik_values_status }) {
    const { t } = useTranslation();
    const [roleBasedSkills, setRoleBasedSkills] = useState([]);
    const [localDraftLoading, setLocalDraftLoading] = useState(false);
    const [localUpdateLoading, setLocalUpdateLoading] = useState(false);
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);
    let options = useGetSkillsHook();
    const educationLevels = useGetEducationLevelsHook();
    const jobTitleOptions = useGetRoleBasedSkillsHook() || [];
    const categoryOptions = useGetJobCategoriesHook() || [];

    // Add event listener for window resize
    useEffect(() => {
        const handleResize = () => {
            setWindowWidth(window.innerWidth);
        };
        
        window.addEventListener('resize', handleResize);
        
        // Clean up the event listener on component unmount
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    // Initialize work experience requirement state
    useEffect(() => {
        // If workExpRequired is not set yet, initialize it based on minworkexp value
        if (formik.values.workExpRequired === undefined) {
            if (formik.values.minworkexp) {
                formik.setFieldValue('workExpRequired', 'required');
            } else {
                formik.setFieldValue('workExpRequired', 'not_required');
                // Reset min/max values if work experience is not required
                formik.setFieldValue('minworkexp', '');
                formik.setFieldValue('maxworkexp', '');
            }
        }
    }, []);

    // Now filter job titles based on the selected category instead of title
    useEffect(() => {
        if (formik.values.category && categoryOptions && categoryOptions.length > 0) {
            // Find the selected category label
            const selectedCategory = categoryOptions.find(cat => cat?.value === formik.values.category);
            if (selectedCategory) {
                // Use the label property which contains the localized category name
                const categoryLabel = selectedCategory.label || '';
                // Also log any other useful properties
                if (selectedCategory.labelen) console.log("Category Label EN:", selectedCategory.labelen);
                if (selectedCategory.labelar) console.log("Category Label AR:", selectedCategory.labelar);

                // Filter job titles that match the category
                if (jobTitleOptions && jobTitleOptions.length > 0) {
                    // Try to find titles related to this category
                    const relatedTitles = jobTitleOptions.filter(title => {
                        if (!title || !title.label) return false;

                        // Check if title label contains category name or vice versa
                        const titleLabel = title.label.toLowerCase();
                        const categoryName = categoryLabel.toLowerCase();
                        const isMatch = titleLabel == categoryName || categoryName == titleLabel;
                        if (isMatch) {
                            console.log(`Match found between category "${categoryName}" and title "${titleLabel}"`);
                        }

                        return isMatch;
                    });
                    // If we found related titles, collect all their skills
                    if (relatedTitles.length > 0) {
                        // Gather all skills from all related titles
                        const allSkills = new Set();
                        relatedTitles.forEach(title => {
                            if (title.skills && Array.isArray(title.skills)) {
                                title.skills.forEach(skill => {
                                    if (skill) allSkills.add(skill);
                                });
                            }
                        });

                        // Convert to format needed for dropdown
                        const skillsArray = Array.from(allSkills).map(skill => ({
                            value: skill,
                            label: skill
                        }));

                        setRoleBasedSkills(skillsArray);
                    } else {
                        // If no direct match, try finding titles that contain words from the category
                        const categoryWords = categoryLabel.toLowerCase().split(/\s+/).filter(w => w.length > 3);

                        if (categoryWords.length > 0) {
                            const partialMatches = jobTitleOptions.filter(title => {
                                if (!title || !title.label) return false;
                                const titleLabel = title.label.toLowerCase();

                                return categoryWords.some(word => titleLabel.includes(word));
                            });

                            if (partialMatches.length > 0) {
                                const allSkills = new Set();
                                partialMatches.forEach(title => {
                                    if (title.skills && Array.isArray(title.skills)) {
                                        title.skills.forEach(skill => {
                                            if (skill) allSkills.add(skill);
                                        });
                                    }
                                });

                                const skillsArray = Array.from(allSkills).map(skill => ({
                                    value: skill,
                                    label: skill
                                }));

                                setRoleBasedSkills(skillsArray);
                            } else {
                                setRoleBasedSkills([]);
                            }
                        } else {
                            setRoleBasedSkills([]);
                        }
                    }
                }
            }
        } else {
            setRoleBasedSkills([]);
        }
    }, [formik.values.category, categoryOptions, jobTitleOptions]);

    const handleSkillsChange = (value) => { formik.setFieldValue('skills', value); }

    const handleRoleSkillsChange = (value) => { formik.setFieldValue('role_skills', value); }

    // Handle work experience requirement change
    const handleWorkExpRequiredChange = (e) => {
        const value = e.target.value;
        formik.setFieldValue('workExpRequired', value);

        if (value === 'required') {
            // Set default values for min and max experience
            formik.setFieldValue('minworkexp', '1');
            formik.setFieldValue('maxworkexp', '2');
        } else {
            // Clear min and max experience fields
            formik.setFieldValue('minworkexp', '');
            formik.setFieldValue('maxworkexp', '');
        }
    };

    const workExpRequiredOptions = [
        { value: 'not_required', label: t('QualificationAndSkills.No_work_experience_required', 'No work experience required') },
        { value: 'required', label: t('QualificationAndSkills.Work_experience_required', 'Work experience required') },
    ];

    return (
        <>
            <div className={styles.row} style={{ margin: `20px 0px 0px 0px`, }}>
                <div className={styles.inputContainer}>
                    <FormSelectField
                        name="skills"
                        type="default"
                        placeholder={t('job_seeker.general_skills')}
                        label={t('job_seeker.general_skills')}
                        isMulti
                        hasBorder
                        options={options}
                        onChange={handleSkillsChange}
                        value={formik.values.skills}
                        error={formik.touched.skills && formik.errors.skills}
                    />
                </div>
                <div className={styles.inputContainer}>
                    <FormSelectField
                        name="role_skills"
                        type="default"
                        placeholder={t('job_seeker.role_based_skills')}
                        label={t('job_seeker.role_based_skills')}
                        isMulti
                        hasBorder
                        options={roleBasedSkills}
                        onChange={handleRoleSkillsChange}
                        value={formik.values.role_skills}
                        error={formik.touched.role_skills && formik.errors.role_skills}
                        disabled={roleBasedSkills.length === 0}
                    />
                </div>
            </div>

            {/* Work Experience Requirement Section */}
            <div className={styles.row}>
                <div className={windowWidth >= 1050 ? styles.halfInputContainer : styles.inputContainer}>
                    <FormOptionField
                        name="workExpRequired"
                        label={t('profile_tabs.work_experience')}
                        type="radio"
                        dark
                        options={workExpRequiredOptions}
                        onChange={handleWorkExpRequiredChange}
                        value={formik.values.workExpRequired || 'not_required'}
                    />
                </div>
                {/* Show min/max experience fields only when work experience is required */}
                {formik.values.workExpRequired === 'required' && (
                    <div className={`${styles.row} ${styles.inputContainer}`}>
                         <div className={`${styles.workExperience} ${styles.workExperienceContainer}`} >
                            <div className={styles.expContainer}>
                                <FormField
                                    name="minworkexp"
                                    type="text"
                                    placeholder={t('QualificationAndSkills.Minimum_experience')}
                                    label={t('QualificationAndSkills.Min_exp', 'Min exp.')}
                                    hasBorder
                                    onChange={formik.handleChange}
                                    labelDirection={windowWidth >= 1050 ? 'right' : undefined}
                                    style={{ paddingLeft: '10px', width: '100%' }}
                                    onBlur={formik.handleBlur}
                                    value={formik.values.minworkexp}
                                    error={formik.touched.minworkexp && formik.errors.minworkexp}
                                />
                            </div>
                            <div className={styles.expContainer}>
                                <FormField
                                    name="maxworkexp"
                                    type="text"
                                    placeholder={t('QualificationAndSkills.Maximum_experience')}
                                    label={t('QualificationAndSkills.Max_exp', 'Max exp.')}
                                    hasBorder
                                    labelDirection={windowWidth >= 1050 ? 'right' : undefined}
                                    onChange={formik.handleChange}
                                    style={{ paddingLeft: '10px', width: '100%' }}
                                    onBlur={formik.handleBlur}
                                    value={formik.values.maxworkexp}
                                    error={formik.touched.maxworkexp && formik.errors.maxworkexp}
                                />
                            </div>
                        </div> 
                    </div>
                )}
            </div>

            <div className={styles.row}>
                <div className={styles.inputContainer}>
                    <div className={styles.aboutTitle}></div>
                    <FormSelectField
                        name="education_level"
                        type="default"
                        placeholder={t('overview.education')}
                        label={t('overview.education')}
                        hasBorder
                        options={educationLevels}
                        onFormikChange={formik.handleChange}
                        value={formik.values.education_level}
                        error={formik.touched.education_level && formik.errors.education_level}
                    />
                </div>
                <div className={styles.inputContainer}>
                    {/* Empty div for layout balance */}
                </div>
            </div>

            <div style={{ display: "flex", gap: 10 }}>
                <BubbleButton dark headerDarkButton
                    onClick={() => {
                        if (formik.values.city && formik.values.country && formik.values.category && formik.values.title) {
                            setActiveSteps(activeSteps - 1)
                        }
                    }}
                    style={{ padding: '10px 20px' }}>{t('signupHeader.back')}</BubbleButton>

                <BubbleButton
                    dark
                    headerDarkButton
                    type="submit"
                    onClick={() => {
                        // Only validate min/max work experience if work experience is required
                        if (formik.values.workExpRequired === 'required') {
                            if (formik.values.maxworkexp && formik.values.minworkexp && formik.values.education_level) {
                                setActiveSteps(activeSteps + 1)
                            }
                        } else {
                            // If work experience is not required, only validate education level
                            if (formik.values.education_level) {
                                setActiveSteps(activeSteps + 1)
                            }
                        }
                    }}
                    style={{ padding: '10px 20px' }}>{t(`jobOverView.Next`)}</BubbleButton>

                {(!id || (formik_values_status === 'draft')) && (
                    <BubbleButton
                        dark
                        headerDarkButton
                        onClick={async () => {
                            setLocalDraftLoading(true);
                            await saveAsDraftOnly();
                            setLocalDraftLoading(false);
                        }}
                        style={{ padding: '10px 20px', marginLeft: '10px' }}
                        disabled={localDraftLoading || localUpdateLoading || draftLoading || submitLoading}
                    >
                        {localDraftLoading ? (
                            <div className={styles.buttonLoader}>
                                <div className={styles.spinnerSmall}></div>
                                <span>{t('forms.login.processing')}</span>
                            </div>
                        ) : (
                            t('job.save_draft', 'Save Draft')
                        )}
                    </BubbleButton>
                )}

                {(id && formik_values_status !== 'draft') && (
                    <BubbleButton
                        dark
                        headerDarkButton
                        onClick={async () => {
                            setLocalUpdateLoading(true);
                            await updateJobOnly();
                            setLocalUpdateLoading(false);
                        }}
                        style={{ padding: '10px 20px', marginLeft: '10px' }}
                        disabled={localDraftLoading || localUpdateLoading || draftLoading || submitLoading}
                    >
                        {localUpdateLoading ? (
                            <div className={styles.buttonLoader}>
                                <div className={styles.spinnerSmall}></div>
                                <span>{t('forms.login.processing')}</span>
                            </div>
                        ) : (
                            t('job.update', 'Update')
                        )}
                    </BubbleButton>
                )}
            </div>
        </>
    );
}

export default QualificationAndSkills;